import { useEffect, useState } from "react";
import { Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { clearDraft } from "@/hooks/useFormDraftCapture";

interface SubmittedPayload {
  draft?: { name?: string; email?: string; phone?: string };
  draftId?: string;
  source?: string;
}

/**
 * Post-submission "Discard draft" prompt. Appears briefly after any
 * data-capture-draft form is successfully submitted, giving the user a
 * one-tap way to wipe their cached info from this device (useful on
 * shared computers / kiosks). Auto-hides after 20s if untouched.
 */
const DiscardDraftPrompt = () => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<SubmittedPayload>({});
  const [discarded, setDiscarded] = useState(false);

  useEffect(() => {
    const onSubmitted = (e: Event) => {
      const detail = (e as CustomEvent).detail as SubmittedPayload | undefined;
      // Only show if there's actually something cached to discard.
      const hasData = Boolean(
        detail?.draft?.name || detail?.draft?.email || detail?.draft?.phone,
      );
      if (!hasData) return;
      setPayload(detail ?? {});
      setDiscarded(false);
      setOpen(true);
      const t = window.setTimeout(() => setOpen(false), 20_000);
      return () => window.clearTimeout(t);
    };
    window.addEventListener("draft-submitted", onSubmitted);
    return () => window.removeEventListener("draft-submitted", onSubmitted);
  }, []);

  if (!open) return null;

  const handleDiscard = async () => {
    await clearDraft(payload.draftId);
    setDiscarded(true);
    void import("@/lib/analytics").then(({ trackEvent }) =>
      trackEvent("draft_discarded", { source: payload.source }),
    );
    window.setTimeout(() => setOpen(false), 1500);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:left-6 sm:bottom-6 z-[60] max-w-sm sm:w-96 rounded-xl border border-border bg-background shadow-xl animate-in slide-in-from-bottom-4 fade-in duration-300"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Dismiss"
        className="absolute top-2 right-2 p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="p-4 sm:p-5">
        {discarded ? (
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-bold text-sm text-foreground">Draft discarded.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your saved info has been cleared from this device.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm text-foreground leading-snug">
                  Quote sent — clear your saved info?
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  We autosaved your name, phone, and email as you typed. On a shared
                  device? Discard the draft to wipe it.
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-10 text-xs gap-1"
                onClick={handleDiscard}
              >
                <Trash2 className="w-3.5 h-3.5" /> Discard draft
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-10 text-xs"
                onClick={() => setOpen(false)}
              >
                Keep on this device
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DiscardDraftPrompt;
